import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";

import { getCurrentReferralSourceDocumentFile, isCurrentReferralSourceDocumentFileLoading, errorLoadingCurrentReferralSourceDocumentFile } from "app/main/referrals/reducers/referrals.reducers";
import { fetchReferral, getReferralSourceDocumentFilePreviewData } from "app/main/referrals/actions/referrals.actions";
import PreviewContent from "components/items/preview-content";
import TabContent from "components/items/tab-content";
import EmptyState from "components/items/empty-state";
import { SourceDocumentIcon, ReferralSourceDocumentRemoveIcon } from "helpers/icon-finder";
import DefaultPanelLayout from "components/items/default-panel-layout";
import { closeDialog, openDialog } from "app/store/actions/dialog.actions";
import { Button, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { red, grey } from "@material-ui/core/colors";
import { showMessage } from "app/store/actions";
import RemoveSourceDocumentForm from "app/main/referrals/components/remove-source-document-form/index";
import formatFileSize from "helpers/format-file-size-byte";

const useStyles = makeStyles(() => ({
  removeSourceDocumentButton: {
    backgroundColor: grey[600],
    color: "#fff",
    "&:hover": {
      backgroundColor: red[700],
    },
  },
}));

const ReferralSourceDocumentPanel = ({
  referral,
  orgUnitId,
  pdfWidth,
}) => {
  const sourceDocumentId = referral.referralSourceDocument?.id;
  const sourceDocumentFileId = referral.referralSourceDocument?.fileId;
  const canRemoveReferralSourceDocument = referral?.permissions?.canRemoveReferralSourceDocument;

  const classes = useStyles();
  const dispatch = useDispatch();
  const sourceDocumentFile = useSelector(getCurrentReferralSourceDocumentFile);
  const isSourceDocumentFileLoading = useSelector(isCurrentReferralSourceDocumentFileLoading);
  const errorLoadingSourceDocument = useSelector(errorLoadingCurrentReferralSourceDocumentFile);

  // Load source document file data when component mounts or dependencies change
  useEffect(() => {
    if (orgUnitId && sourceDocumentId && sourceDocumentFileId) {
      dispatch(getReferralSourceDocumentFilePreviewData(orgUnitId, sourceDocumentId, sourceDocumentFileId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUnitId && sourceDocumentId && sourceDocumentFileId]);

  /**
   * Handle successful deletion of source document
   * @param {Object} response - Server response after successful deletion
   */
  const handleSuccessfulDeletion = response => {
    // Refresh referral data
    dispatch(fetchReferral(orgUnitId, referral.id));

    // Close the dialog
    dispatch(closeDialog());

    // Show success message
    dispatch(showMessage({
      variant: "success",
      message: `Referral Source Document "${response.removedReferralSourceDocumentName}" 
      successfully removed by "${response.removedBy}" `,
    }));
  };

  /**
   * Handle the removal of source document
   */
  const handleDeleteSourceDocument = () => {
    dispatch(openDialog({
      children: (
        <RemoveSourceDocumentForm
          orgUnitId={orgUnitId}
          referralId={referral.id}
          sourceDocumentId={sourceDocumentId}
          onSucceed={handleSuccessfulDeletion}
        />
      ),
      // Optional: Add dialog configuration if your dialog component supports it
      maxWidth: "sm",
      fullWidth: true,
    }));
  };

  /**
   * Render empty state when no source document is available
   */
  const renderEmptyState = () => (
    <EmptyState
      icon={<SourceDocumentIcon />}
      title="No Source Document"
    />
  );

  /**
   * Check if source document exists
   */
  const hasSourceDocument = !isEmpty(sourceDocumentFile);

  return (
    <TabContent
      withGutters={false} // Set to false to match attachment panel styling
      loading={isSourceDocumentFileLoading}
      empty={!hasSourceDocument}
      error={errorLoadingSourceDocument}
      emptyState={renderEmptyState()}
    >
      {/* Wrap the content with DefaultPanelLayout to add title section */}
      <DefaultPanelLayout
        title="Source Document"
        icon={<SourceDocumentIcon />} // Use custom icon or fallback to string icon
        isEmpty={!hasSourceDocument} // Use the same logic as TabContent
        // Only show delete button if:
        // 1. User has permission to delete
        // 2. There is actually a source document to delete (using the original logic)
        headerRightContent={canRemoveReferralSourceDocument && hasSourceDocument && (
          <Button
            variant="contained"
            size="small"
            className={classes.removeSourceDocumentButton}
            startIcon={<ReferralSourceDocumentRemoveIcon />}
            title="Remove Source Document"
            onClick={handleDeleteSourceDocument}
          >
            Remove
          </Button>
        )}
      >
        {/* The actual document preview content */}
        <PreviewContent
          fileData={sourceDocumentFile?.base64File || sourceDocumentFile?.htmlPreview}
          mimeType={sourceDocumentFile?.mimeType}
          pdfProps={{ width: pdfWidth }}
          hasPreview={sourceDocumentFile?.hasPreview}
        />
        {sourceDocumentFile?.fileSize != null && sourceDocumentFile.fileSize > 0 && <Typography variant="caption">File Size: {formatFileSize(sourceDocumentFile.fileSize)}</Typography>}
      </DefaultPanelLayout>
    </TabContent>
  );
};

export default ReferralSourceDocumentPanel;
